import { useEffect, useMemo, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  YoutubeLogo,
  Certificate,
  CheckCircle,
  ArrowSquareOut,
  MagnifyingGlass,
  Star,
  Funnel,
  BookmarkSimple,
  X,
  Clock,
  CurrencyCircleDollar,
  ShieldCheck,
  GraduationCap,
  Briefcase,
  Buildings,
  Sparkle,
  ArrowRight,
  Check,
  WarningCircle,
  Quotes,
  CalendarBlank,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { hasPlan } from "@/lib/planAccess";
import { CERTIFICATIONS, FREE_LEARNING } from "@/data/certifications";
import { FREE_CERTIFICATIONS } from "@/data/freeCertifications";

const DAILY_MOTIVATIONS = [
  { quote: "Small progress each day adds up to big results.", sub: "Keep learning. Keep growing." },
  { quote: "Your next opportunity is built by what you learn today.", sub: "Keep building your edge." },
  { quote: "Consistency turns knowledge into capability.", sub: "One focused step at a time." },
  { quote: "Learn something today that makes tomorrow easier.", sub: "Invest in your future self." },
  { quote: "The skills you build today become the confidence you carry tomorrow.", sub: "Keep moving forward." },
  { quote: "Progress does not need to be perfect to be powerful.", sub: "Stay curious. Stay consistent." },
  { quote: "Every skill mastered is another door you can open.", sub: "Keep expanding your possibilities." },
  { quote: "A better career starts with a better next step.", sub: "Choose your next step wisely." },
  { quote: "Learning compounds when you show up every day.", sub: "Make today's effort count." },
  { quote: "You do not need to know everything. You need to keep learning.", sub: "Progress is the goal." },
  { quote: "Build skills that make your future self proud.", sub: "Your effort today matters." },
  { quote: "Small wins become big career moves over time.", sub: "Keep the momentum going." },
  { quote: "The strongest career advantage is the habit of learning.", sub: "Never stop improving." },
  { quote: "Turn curiosity into skill, and skill into opportunity.", sub: "Keep exploring." },
  { quote: "Your career can change with one useful skill at a time.", sub: "Start with the next one." },
];

const getDailyMotivation = () => {
  const day = Math.floor(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()) / 86400000);
  return DAILY_MOTIVATIONS[((day % DAILY_MOTIVATIONS.length) + DAILY_MOTIVATIONS.length) % DAILY_MOTIVATIONS.length];
};

const PROVIDER_LOGOS = {
  Autodesk: "https://www.google.com/s2/favicons?domain=autodesk.com&sz=128",
  Microsoft: "https://www.google.com/s2/favicons?domain=microsoft.com&sz=128",
  "Microsoft Azure": "https://www.google.com/s2/favicons?domain=microsoft.com&sz=128",
  Azure: "https://www.google.com/s2/favicons?domain=microsoft.com&sz=128",
  Cisco: "https://www.google.com/s2/favicons?domain=cisco.com&sz=128",
  AWS: "https://www.google.com/s2/favicons?domain=aws.amazon.com&sz=128",
  "Amazon Web Services": "https://www.google.com/s2/favicons?domain=aws.amazon.com&sz=128",
  "Google Cloud": "https://www.google.com/s2/favicons?domain=cloud.google.com&sz=128",
  "Google for Education": "https://www.google.com/s2/favicons?domain=edu.google.com&sz=128",
  Google: "https://www.google.com/s2/favicons?domain=google.com&sz=128",
  CompTIA: "https://www.google.com/s2/favicons?domain=comptia.org&sz=128",
  IBM: "https://www.google.com/s2/favicons?domain=ibm.com&sz=128",
  Oracle: "https://www.google.com/s2/favicons?domain=oracle.com&sz=128",
  "Red Hat": "https://www.google.com/s2/favicons?domain=redhat.com&sz=128",
  GitHub: "https://www.google.com/s2/favicons?domain=github.com&sz=128",
  Unity: "https://www.google.com/s2/favicons?domain=unity.com&sz=128",
  NVIDIA: "https://www.google.com/s2/favicons?domain=nvidia.com&sz=128",
  Salesforce: "https://www.google.com/s2/favicons?domain=salesforce.com&sz=128",
  HubSpot: "https://www.google.com/s2/favicons?domain=hubspot.com&sz=128",
  Adobe: "https://www.google.com/s2/favicons?domain=adobe.com&sz=128",
  MongoDB: "https://www.google.com/s2/favicons?domain=mongodb.com&sz=128",
  HashiCorp: "https://www.google.com/s2/favicons?domain=hashicorp.com&sz=128",
  "Python Institute": "https://www.google.com/s2/favicons?domain=pythoninstitute.org&sz=128",
  Tableau: "https://www.google.com/s2/favicons?domain=tableau.com&sz=128",
  Snowflake: "https://www.google.com/s2/favicons?domain=snowflake.com&sz=128",
  Databricks: "https://www.google.com/s2/favicons?domain=databricks.com&sz=128",
  Meta: "https://www.google.com/s2/favicons?domain=meta.com&sz=128",
  "CFA Institute": "https://www.google.com/s2/favicons?domain=cfainstitute.org&sz=128",
  GARP: "https://www.google.com/s2/favicons?domain=garp.org&sz=128",
  "The IIA": "https://www.google.com/s2/favicons?domain=theiia.org&sz=128",
  IMA: "https://www.google.com/s2/favicons?domain=imanet.org&sz=128",
  AICPA: "https://www.google.com/s2/favicons?domain=aicpa-cima.com&sz=128",
  AAPC: "https://www.google.com/s2/favicons?domain=aapc.com&sz=128",
  IAPP: "https://www.google.com/s2/favicons?domain=iapp.org&sz=128",
  GBCI: "https://www.google.com/s2/favicons?domain=usgbc.org&sz=128",
  IWBI: "https://www.google.com/s2/favicons?domain=wellcertified.com&sz=128",
  ISC2: "https://www.google.com/s2/favicons?domain=isc2.org&sz=128",
  ASQ: "https://www.google.com/s2/favicons?domain=asq.org&sz=128",
  NCEES: "https://www.google.com/s2/favicons?domain=ncees.org&sz=128",
  PMI: "https://www.google.com/s2/favicons?domain=pmi.org&sz=128",
  RICS: "https://www.google.com/s2/favicons?domain=rics.org&sz=128",
  "AACE International": "https://www.google.com/s2/favicons?domain=aacei.org&sz=128",
  buildingSMART: "https://www.google.com/s2/favicons?domain=buildingsmart.org&sz=128",
  USGBC: "https://www.google.com/s2/favicons?domain=usgbc.org&sz=128",
  PeopleCert: "https://www.google.com/s2/favicons?domain=peoplecert.org&sz=128",
  HRCI: "https://www.google.com/s2/favicons?domain=hrci.org&sz=128",
  SHRM: "https://www.google.com/s2/favicons?domain=shrm.org&sz=128",
  NEBOSH: "https://www.google.com/s2/favicons?domain=nebosh.org.uk&sz=128",
  ACCA: "https://www.google.com/s2/favicons?domain=accaglobal.com&sz=128",
  ASCM: "https://www.google.com/s2/favicons?domain=ascm.org&sz=128",
  AHIMA: "https://www.google.com/s2/favicons?domain=ahima.org&sz=128",
  AHA: "https://www.google.com/s2/favicons?domain=aha.org&sz=128",
  NAHQ: "https://www.google.com/s2/favicons?domain=nahq.org&sz=128",
  AONL: "https://www.google.com/s2/favicons?domain=aonl.org&sz=128",
  HFMA: "https://www.google.com/s2/favicons?domain=hfma.org&sz=128",
  "OpenLearn – The Open University": "https://www.google.com/s2/favicons?domain=open.edu&sz=128",
  OpenLearn: "https://www.google.com/s2/favicons?domain=open.edu&sz=128",
};

const PROVIDER_ALIASES = {
  "microsoft certified": "Microsoft",
  "microsoft azure": "Microsoft",
  "microsoft corporation": "Microsoft",
  "amazon web services": "AWS",
  aws: "AWS",
  "google cloud platform": "Google Cloud",
  "google for education": "Google for Education",
  isc2: "ISC2",
  "isc²": "ISC2",
  "openlearn – the open university": "OpenLearn – The Open University",
};

const COUNTRY_FLAGS = {
  india: "🇮🇳",
  "united arab emirates": "🇦🇪",
  uae: "🇦🇪",
  "u.a.e": "🇦🇪",
  "united states": "🇺🇸",
  "united states of america": "🇺🇸",
  usa: "🇺🇸",
  us: "🇺🇸",
  "united kingdom": "🇬🇧",
  uk: "🇬🇧",
  britain: "🇬🇧",
  "saudi arabia": "🇸🇦",
  qatar: "🇶🇦",
  kuwait: "🇰🇼",
  bahrain: "🇧🇭",
  oman: "🇴🇲",
  canada: "🇨🇦",
  australia: "🇦🇺",
  singapore: "🇸🇬",
  germany: "🇩🇪",
  france: "🇫🇷",
  netherlands: "🇳🇱",
  ireland: "🇮🇪",
  "new zealand": "🇳🇿",
  "south africa": "🇿🇦",
  malaysia: "🇲🇾",
  japan: "🇯🇵",
};

const getCountryFlag = (country) =>
  COUNTRY_FLAGS[normalize(country)] || "🌍";

const CAREER_ALIASES = {
  doctor: ["doctor", "physician", "medical doctor"],
  physician: ["physician", "doctor", "medical doctor"],
  "medical doctor": ["medical doctor", "physician", "doctor"],
  nurse: ["nurse", "registered nurse", "nursing"],
  "software engineer": ["software engineer", "software developer", "application developer"],
  "network engineer": ["network engineer", "network administrator", "network specialist"],
  "cybersecurity engineer": ["cybersecurity engineer", "security engineer", "cybersecurity analyst"],
  "data scientist": ["data scientist", "machine learning engineer", "data analyst"],
  "data analyst": ["data analyst", "business analyst", "data scientist"],
  "project manager": ["project manager", "program manager", "project management"],
  "bim engineer": ["bim engineer", "bim modeler", "bim coordinator", "building information modeling"],
  "bim modeler": ["bim modeler", "bim engineer", "bim coordinator"],
  "revit engineer": ["revit", "bim engineer", "bim modeler"],
  architect: ["architect", "architectural designer", "architectural design"],
};

const GENERIC_CAREER_WORDS = new Set([
  "engineer", "manager", "specialist", "professional", "designer",
  "developer", "administrator", "associate", "architect", "consultant",
  "coordinator", "analyst", "officer", "lead", "senior", "junior",
]);


const CATEGORIES = [
  "All",
  "Architecture, BIM & Construction",
  "Engineering",
  "Software & Programming",
  "IT & Networking",
  "Cybersecurity",
  "Cloud & DevOps",
  "AI & Machine Learning",
  "Data & Analytics",
  "Project & Business Management",
  "Finance & Accounting",
  "Healthcare",
  "HR",
  "Marketing & Sales",
  "Design & Creative",
  "Manufacturing & Supply Chain",
  "Energy & Environment",
  "Education",
  "Other",
];

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

const normalize = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const tokens = (value = "") =>
  normalize(value)
    .split(/\s+/)
    .filter((x) => x.length > 2);

function scoreLearningCourse(course, profile, roadmap) {
  const target = normalize(profile?.desired_career || "");
  const current = normalize(profile?.current_job || "");
  const targetAliases = CAREER_ALIASES[target] || (target ? [target] : []);
  const courseText = normalize(
    [
      course?.title,
      course?.channel,
      course?.search_query,
      ...(Array.isArray(course?.skills) ? course.skills : []),
    ].filter(Boolean).join(" ")
  );

  const roadmapText = normalize(JSON.stringify(roadmap || {}));
  let score = 0;

  targetAliases.forEach((alias) => {
    const a = normalize(alias);
    if (a && courseText.includes(a)) score += 100;
  });

  tokens(target).forEach((word) => {
    if (!GENERIC_CAREER_WORDS.has(word) && courseText.includes(word)) score += 25;
  });

  tokens(current).forEach((word) => {
    if (!GENERIC_CAREER_WORDS.has(word) && courseText.includes(word)) score += 8;
  });

  if (course?.title && roadmapText.includes(normalize(course.title))) score += 300;
  if (course?.search_query && roadmapText.includes(normalize(course.search_query))) score += 150;

  return score;
}

function scoreCertification(cert, profile, roadmap) {
  const target = normalize(profile?.desired_career || "");
  const targetAliases = CAREER_ALIASES[target] || [target];
  const careerPaths = (cert.careerPaths || []).map(normalize);
  const certName = normalize(cert.name);

  const roadmapCerts = Array.isArray(roadmap?.recommended_certifications)
    ? roadmap.recommended_certifications.map(normalize).filter(Boolean)
    : [];

  let score = 0;

  // The AI roadmap is the strongest signal. Only an actual certification
  // named by the roadmap receives this boost.
  if (roadmapCerts.some((name) =>
    name === certName || name.includes(certName) || certName.includes(name)
  )) {
    score += 1000;
  }

  if (target) {
    // Exact career-path match is intentionally much stronger than a generic
    // word such as "engineer" or "manager".
    if (careerPaths.some((path) =>
      targetAliases.some((alias) => path === normalize(alias))
    )) {
      score += 500;
    } else if (careerPaths.some((path) =>
      targetAliases.some((alias) =>
        path.includes(normalize(alias)) || normalize(alias).includes(path)
      )
    )) {
      score += 350;
    } else {
      const targetWords = tokens(target).filter(
        (word) => !GENERIC_CAREER_WORDS.has(word)
      );
      const matchedWords = targetWords.filter((word) =>
        careerPaths.some((path) => path.includes(word))
      );

      if (targetWords.length > 0 && matchedWords.length === targetWords.length) {
        score += 250;
      }
    }
  }

  return score;
}


const normalizeCertification = (cert) => {
  const skills = Array.isArray(cert.skills) ? cert.skills : [];
  const careerPaths = Array.isArray(cert.careerPaths)
    ? cert.careerPaths
    : [];
  const level = cert.level || "Professional";

  const difficulty =
    cert.difficulty ||
    (level === "Associate" || level === "User"
      ? "Beginner"
      : level === "Professional" ||
          level === "Specialist" ||
          level === "Specialty" ||
          level === "Advanced" ||
          level === "PCPP1"
        ? "Advanced"
        : "Intermediate");

  return {
    ...cert,
    provider: cert.provider || "Certification Provider",
    category: cert.category || "Other",
    description:
      cert.description ||
      "Industry-recognized certification credential.",
    skills,
    careerPaths,
    level,
    difficulty,
    cost: cert.cost || "See official website",
    duration: cert.duration || "Self-paced",
    careerValue: cert.careerValue || "Relevant",
    recommendedFor:
      cert.recommendedFor ||
      (careerPaths.length
        ? careerPaths.join(", \ ")
        : "Professionals in this field"),
    eligibility:
      cert.eligibility || "See official requirements",
    prepUrl: cert.prepUrl || cert.officialUrl || "#",
    officialUrl: cert.officialUrl || cert.prepUrl || "#",
  };
};

function ProviderLogo({ provider }) {
  const [failed, setFailed] = useState(false);
  const name = String(provider || "").trim();
  const normalized = name.toLowerCase().replace(/[^a-z0-9²]+/g, " ").trim();
  const src =
    PROVIDER_LOGOS[name] ||
    (PROVIDER_ALIASES[normalized] ? PROVIDER_LOGOS[PROVIDER_ALIASES[normalized]] : null) ||
    (normalized.includes("microsoft") ? PROVIDER_LOGOS.Microsoft : null) ||
    (normalized.includes("azure") ? PROVIDER_LOGOS.Azure : null) ||
    (normalized.includes("amazon") || normalized === "aws" ? PROVIDER_LOGOS.AWS : null) ||
    (normalized.includes("google cloud") ? PROVIDER_LOGOS["Google Cloud"] : null) ||
    (normalized.includes("google") ? PROVIDER_LOGOS.Google : null) ||
    (normalized.includes("autodesk") ? PROVIDER_LOGOS.Autodesk : null) ||
    (normalized.includes("cisco") ? PROVIDER_LOGOS.Cisco : null) ||
    (normalized.includes("isc2") || normalized.includes("isc²") ? PROVIDER_LOGOS.ISC2 : null) ||
    (normalized.includes("red hat") ? PROVIDER_LOGOS["Red Hat"] : null) ||
    (normalized.includes("openlearn") ? PROVIDER_LOGOS["OpenLearn – The Open University"] : null);

  if (!src || failed) {
    return (
      <span className="text-xs font-bold text-blue-300">
        {(provider || "CP").slice(0, 2).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={`${provider} logo`}
      className="h-7 w-7 object-contain"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function Badge({ children, tone = "blue" }) {
  const classes = {
    blue: "border-blue-400/30 bg-blue-500/10 text-blue-300",
    gold: "border-amber-400/30 bg-amber-400/10 text-amber-300",
    green: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
    slate: "border-white/10 bg-white/[0.04] text-zinc-400",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-1 text-[10px] font-medium ${
        classes[tone] || classes.blue
      }`}
    >
      {children}
    </span>
  );
}

export default function Learning() {
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");

  const [rm, setRm] = useState(null);
  const [progress, setProgress] = useState({
    completed: [],
    count: 0,
  });

  const [tab, setTab] = useState("recommended");

  useEffect(() => { if (!paid && tab === "recommended") setTab("free"); }, [paid, tab]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [difficulty, setDifficulty] = useState("All");
  const [cost, setCost] = useState("All");
  const [provider, setProvider] = useState("All");
  const [selected, setSelected] = useState(null);
  const [sort, setSort] = useState("relevance");

  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem(
          "careerGpsSavedCertifications"
        ) || "[]"
      );
    } catch {
      return [];
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roadmap");
        setRm(data);
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error("roadmap load failed", err);
        }
      }

      try {
        const { data } = await api.get("/learning/progress");
        setProgress(data);
      } catch (err) {
        console.error(
          "learning progress load failed",
          err
        );
      }
    })();
  }, []);

  const markComplete = async (id, title) => {
    try {
      await api.post("/learning/complete", {
        lesson_id: id,
        title,
      });

      const { data } = await api.get(
        "/learning/progress"
      );

      setProgress(data);

      toast.success("Completed! +20 XP");
    } catch {
      toast.error("Failed to save progress");
    }
  };

  const isDone = (id) =>
    progress.completed.some(
      (c) => c.lesson_id === id
    );

  const isSaved = (id) => saved.includes(id);

  const toggleSaved = (id) => {
    const next = isSaved(id)
      ? saved.filter((x) => x !== id)
      : [...saved, id];

    setSaved(next);

    localStorage.setItem(
      "careerGpsSavedCertifications",
      JSON.stringify(next)
    );

    toast.success(
      isSaved(id)
        ? "Removed from saved"
        : "Certification saved"
    );
  };

  const profile = user?.profile || {};

  const normalizedCertifications = useMemo(
    () => CERTIFICATIONS.map(normalizeCertification),
    []
  );

  const providers = useMemo(
    () => [
      "All",
      ...Array.from(
        new Set(
          normalizedCertifications.map(
            (c) => c.provider
          )
        )
      ),
    ],
    [normalizedCertifications]
  );

  const rankedCerts = useMemo(
    () =>
      normalizedCertifications
        .map((cert) => ({
          ...cert,
          relevance: scoreCertification(
            cert,
            profile,
            rm
          ),
        }))
        .sort((a, b) => {
          if (sort === "name") {
            return a.name.localeCompare(b.name);
          }

          if (sort === "career") {
            return (
              (b.careerValue === "High" ? 1 : 0) -
              (a.careerValue === "High" ? 1 : 0)
            );
          }

          return b.relevance - a.relevance;
        }),
    [normalizedCertifications, profile, rm, sort]
  );

  const filtered = useMemo(
    () =>
      rankedCerts.filter((cert) => {
        const q = normalize(query);

        const searchable = normalize(
          [
            cert.name,
            cert.provider,
            cert.category,
            cert.description,
            ...cert.skills,
            ...cert.careerPaths,
          ].join(" ")
        );

        if (q) {
          const queryTokens = tokens(q);
          if (
            queryTokens.length > 0 &&
            !queryTokens.every((token) =>
              searchable.includes(token)
            )
          ) {
            return false;
          }
        }

        if (
          category !== "All" &&
          cert.category !== category
        ) {
          return false;
        }

        if (
          difficulty !== "All" &&
          cert.difficulty !== difficulty
        ) {
          return false;
        }

        if (
          cost === "Free" &&
          !String(cert.cost || "")
            .toLowerCase()
            .includes("free")
        ) {
          return false;
        }

        if (
          cost === "Paid" &&
          String(cert.cost || "")
            .toLowerCase()
            .includes("free")
        ) {
          return false;
        }

        if (
          provider !== "All" &&
          cert.provider !== provider
        ) {
          return false;
        }

        return true;
      }),
    [
      rankedCerts,
      query,
      category,
      difficulty,
      cost,
      provider,
    ]
  );

 

const searchResults = useMemo(() => {
  if (!query.trim()) return [];

  const queryTokens = tokens(query);

  return rankedCerts
    .map((cert) => {
      const nameText = normalize(cert.name);
      const careerText = normalize(cert.careerPaths.join(" "));
      const skillText = normalize(cert.skills.join(" "));
      const providerText = normalize(cert.provider);
      const categoryText = normalize(cert.category);
      const descriptionText = normalize(cert.description);

      let searchScore = 0;

      queryTokens.forEach((token) => {
        if (nameText.includes(token)) searchScore += 60;
        if (careerText.includes(token)) searchScore += 50;
        if (skillText.includes(token)) searchScore += 40;
        if (providerText.includes(token)) searchScore += 35;
        if (categoryText.includes(token)) searchScore += 30;
        if (descriptionText.includes(token)) searchScore += 20;
      });

      return {
        ...cert,
        searchScore,
      };
    })
    .filter((cert) => cert.searchScore > 0)
    .sort((a, b) => b.searchScore - a.searchScore);
}, [query, rankedCerts]);
  
const roadmapRecommended = useMemo(() => {
  if (!profile?.desired_career && !rm) return [];

  // Default view is intentionally strict: only certifications explicitly
  // named by the roadmap OR directly aligned with the desired career path.
  return rankedCerts
    .filter((cert) => cert.relevance >= 250)
    .slice(0, 6);
}, [rankedCerts, profile?.desired_career, rm]);

const displayed = query.trim()
  ? searchResults
  : tab === "recommended"
    ? roadmapRecommended
    : filtered;

  const courses = Array.isArray(rm?.recommended_youtube_courses)
    ? rm.recommended_youtube_courses
    : [];

  const learningCourses = useMemo(() => {
    const q = normalize(query);

    return courses
      .map((course, index) => ({
        ...course,
        _id: course.id || `yt-${index}`,
        _score: scoreLearningCourse(course, profile, rm),
      }))
      .filter((course) => {
        if (!q) return true;
        const searchable = normalize(
          [
            course.title,
            course.channel,
            course.search_query,
            ...(Array.isArray(course.skills) ? course.skills : []),
          ].filter(Boolean).join(" ")
        );
        return tokens(q).every((token) => searchable.includes(token));
      })
      .sort((a, b) => b._score - a._score)
      .slice(0, 6);
  }, [courses, query, profile, rm]);

  const ytUrl = (q) =>
    `https://www.youtube.com/results?search_query=${encodeURIComponent(
      q
    )}`;

  const getYoutubeVideoId = (course) => {
    const directId = course?.video_id || course?.videoId || course?.youtube_id || course?.youtubeId;
    if (directId) return String(directId).trim();

    const rawUrl = course?.youtube_url || course?.youtubeUrl || course?.video_url || course?.videoUrl || course?.url || course?.link || "";
    const match = String(rawUrl).match(/(?:v=|youtu\.be\/|youtube\.com\/(?:embed|shorts)\/)([A-Za-z0-9_-]{11})/);
    return match ? match[1] : "";
  };

  const getYoutubeWatchUrl = (course) => {
    const videoId = getYoutubeVideoId(course);
    if (videoId) return `https://www.youtube.com/watch?v=${videoId}`;
    return course?.youtube_url || course?.youtubeUrl || course?.video_url || course?.videoUrl || course?.url || ytUrl(course?.search_query || course?.title || "");
  };

  const getCourseLevel = (course) => {
    if (course?.level) return course.level;
    if (course?.difficulty) return course.difficulty;
    return "Job-ready";
  };

  const getCourseDuration = (course) =>
    course?.duration || course?.estimated_duration || course?.estimatedDuration || "Self-paced";

  const getCourseWhy = (course) => {
    const career = profile?.desired_career || "career goal";
    const skills = Array.isArray(course?.skills)
      ? course.skills.filter(Boolean).slice(0, 3)
      : [];
    const topic = course?.title || course?.search_query || "this course";
    const skillText = skills.length ? skills.join(", ") : topic;

    if (skills.length >= 2) {
      return `Builds ${skillText} skills that are useful for your ${career} career path.`;
    }

    if (skills.length === 1) {
      return `Strengthens ${skillText} skills that support your ${career} career path.`;
    }

    return `Covers ${topic}, giving you practical skills relevant to your ${career} career path.`;
  };

  return (
    <div
      className="min-h-screen bg-[#05070a] text-zinc-100"
      data-testid="learning-page"
    >
      <Navbar />
      <DashSidebar />

      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-8 xl:px-10 pb-24 max-w-[1500px] mx-auto lg:mx-0">

        {/* HERO */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-[#071321] via-[#08131f] to-[#0b1723] overflow-hidden mb-7">
          <div className="relative p-6 sm:p-8">

            <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_70%_40%,rgba(30,120,220,.24),transparent_60%)] pointer-events-none" />

            <div className="relative w-full">

              <Badge tone="blue">
                <GraduationCap
                  size={13}
                  className="mr-1"
                />
                LEARN & GET CERTIFIED
              </Badge>

              <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium mt-4">
  Certifications & Credentials
              </h1>

              <p className="text-zinc-400 mt-3 max-w-2xl">
                Find certifications that fit your career direction and development plan.
              </p>

              {(() => {
                const motivation = getDailyMotivation();
                return (
                  <div className="mt-6 rounded-2xl border border-blue-400/40 bg-[#071321]/70 overflow-hidden w-full">
                    <div className="flex flex-col lg:flex-row items-stretch">
                      <div className="flex items-center px-4 sm:px-6 py-4 sm:py-5 lg:border-r border-blue-400/30">
                        <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full border border-blue-400/20 bg-blue-400/[0.04] flex items-center justify-center shrink-0">
                          <Quotes size={28} weight="fill" className="text-blue-300" />
                        </div>
                      </div>

                      <div className="flex-1 px-5 sm:px-7 py-4 sm:py-5 flex flex-col justify-center min-w-0">
                        <div className="text-sm sm:text-base md:text-lg font-medium tracking-tight text-zinc-100 leading-6 md:leading-7">
                          “{motivation.quote}”
                        </div>
                        <div className="text-xs sm:text-sm text-zinc-500 mt-1.5">
                          {motivation.sub}
                        </div>
                      </div>

                      <div className="lg:w-52 xl:w-56 border-t lg:border-t-0 lg:border-l border-blue-400/30 px-5 sm:px-6 py-4 sm:py-5 flex items-center gap-3 shrink-0">
                        <CalendarBlank size={22} className="text-blue-300 shrink-0" />
                        <div>
                          <div className="text-[9px] uppercase tracking-widest text-blue-200/80 font-medium">
                            Daily Motivation
                          </div>
                          <div className="text-[11px] text-zinc-500 mt-1">
                            A new quote every day
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* SEARCH */}
        <div className="flex flex-col xl:flex-row gap-3 mb-5">

          <div className="relative flex-1">
            <MagnifyingGlass
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
            />

            <input
              value={query}
              onChange={(e) =>
                setQuery(e.target.value)
              }
              placeholder="Search by career, designation, skill or certification..."
              className="w-full bg-[#0c1118] border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm outline-none focus:border-blue-500/50"
            />
          </div>

          <select
            value={sort}
            onChange={(e) =>
              setSort(e.target.value)
            }
            className="bg-[#0c1118] border border-white/10 rounded-xl px-3 py-3 text-sm outline-none"
          >
            <option value="relevance">
              Sort: Relevance
            </option>
            <option value="career">
              Career value
            </option>
            <option value="name">
              Name
            </option>
          </select>

        </div>

        <div className="grid xl:grid-cols-[190px_1fr] gap-5">

          {/* FILTERS */}
          <aside className="rounded-2xl border border-white/10 bg-[#0b0f14] p-4 h-fit xl:sticky xl:top-24">

            <div className="flex items-center gap-2 font-medium mb-5">
              <Funnel
                size={17}
                className="text-blue-300"
              />
              Filter Certifications
            </div>

            <div className="space-y-5">

              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Category
                </div>

                <div className="space-y-2 max-h-56 overflow-auto pr-1">

                  {CATEGORIES.map(
                    (item) => (
                      <button
                        key={item}
                        onClick={() =>
                          setCategory(item)
                        }
                        className={`block w-full text-left text-xs rounded-lg px-2 py-1.5 ${
                          category === item
                            ? "bg-blue-500/15 text-blue-300"
                            : "text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}

                </div>
              </div>

              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Difficulty
                </div>

                {LEVELS.map(
                  (item) => (
                    <button
                      key={item}
                      onClick={() =>
                        setDifficulty(
                          difficulty === item
                            ? "All"
                            : item
                        )
                      }
                      className={`mr-1 mb-1 text-xs rounded-full border px-2 py-1 ${
                        difficulty === item
                          ? "border-blue-400/40 bg-blue-500/10 text-blue-300"
                          : "border-white/10 text-zinc-500"
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>

              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Cost
                </div>

                {[
                  "All",
                  "Free",
                  "Paid",
                ].map(
                  (item) => (
                    <button
                      key={item}
                      onClick={() =>
                        setCost(item)
                      }
                      className={`mr-1 mb-1 text-xs rounded-full border px-2 py-1 ${
                        cost === item
                          ? "border-blue-400/40 bg-blue-500/10 text-blue-300"
                          : "border-white/10 text-zinc-500"
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>

              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Provider
                </div>

                <select
                  value={provider}
                  onChange={(e) =>
                    setProvider(
                      e.target.value
                    )
                  }
                  className="w-full bg-[#0c1118] border border-white/10 rounded-lg px-2 py-2 text-xs outline-none"
                >
                  {providers.map(
                    (item) => (
                      <option key={item}>
                        {item}
                      </option>
                    )
                  )}
                </select>
              </div>

              <button
                onClick={() => {
                  setCategory("All");
                  setDifficulty("All");
                  setCost("All");
                  setProvider("All");
                  setQuery("");
                }}
                className="w-full border border-white/10 rounded-xl py-2 text-xs text-zinc-400 hover:text-white"
              >
                Reset Filters
              </button>

            </div>
          </aside>

          {/* CONTENT */}
          <section className="min-w-0">

            {/* TABS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 rounded-xl border border-white/10 overflow-hidden bg-[#0b1118] mb-6">

              {[
                [
                  "recommended",
                  "Recommended for You",
                  Star,
                ],
                [
                  "industry",
                  "Industry Certifications",
                  Certificate,
                ],
                [
                  "free",
                  "Free Learning",
                  Certificate,
                ],
                [
                  "free-certification",
                  "Free Certification",
                  Certificate,
                ],
              ].map(
                ([id, label, Icon]) => (
                  <button
                    key={id}
                    onClick={() => {
                      if ((id === "recommended" || id === "industry") && !paid) return;
                      setTab(id);
                    }}
                    className={`px-3 py-3 text-xs sm:text-sm flex items-center justify-center gap-2 border-r border-white/10 ${
                      tab === id
                        ? "bg-blue-600/90 text-white"
                        : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                    {(id === "recommended" || id === "industry") && !paid && <span className="text-[9px]">🔒</span>}
                  </button>
                )
              )}

            </div>

            {/* FREE LEARNING */}
            {tab === "free" ? (
              <div className="space-y-10">

                <section>
                  <div className="flex items-end justify-between mb-4 gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Certificate size={21} className="text-blue-300" />
                        <h2 className="text-xl font-semibold">
                          Free Learning & Certificates
                        </h2>
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">
                        Official resources you can use to build skills and prepare before paying for an exam.
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FREE_LEARNING
                      .filter((item) => {
                        const q = normalize(query);
                        if (!q) return true;
                        const searchable = normalize(
                          [item.name, item.provider, item.category, item.description]
                            .filter(Boolean)
                            .join(" ")
                        );
                        return tokens(q).every((token) => searchable.includes(token));
                      })
                      .map((item) => (
                        <article
                          key={item.id}
                          className="rounded-2xl border border-white/10 bg-[#0c1118] p-5 hover:border-blue-400/30 hover:-translate-y-0.5 transition-all flex flex-col"
                        >
                          <Badge tone="green">FREE LEARNING</Badge>
                          <h3 className="font-semibold mt-4">{item.name}</h3>
                          <div className="text-[11px] text-zinc-500 mt-1">
                            {item.provider} · {item.category}
                          </div>
                          <p className="text-sm text-zinc-400 mt-3 leading-6 min-h-16">
                            {item.description}
                          </p>
                          <a
                            href={item.officialUrl || item.url || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-4 w-full rounded-xl bg-blue-600/10 border border-blue-500/30 text-blue-300 py-2.5 inline-flex items-center justify-center gap-2 text-sm"
                          >
                            Open Official Resource
                            <ArrowSquareOut size={14} />
                          </a>
                        </article>
                      ))}
                  </div>
                </section>

                <section>
                  <div className="flex items-end justify-between mb-4 gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <YoutubeLogo size={21} className="text-red-400" />
                        <h2 className="text-xl font-semibold">YouTube Learning Paths</h2>
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">
                        Free video courses selected from your AI roadmap to help you build practical, job-ready skills.
                      </p>
                    </div>
                  </div>

                  {learningCourses.length === 0 ? (
                    <div className="rounded-2xl border border-white/10 bg-[#0c1118] p-8 text-center">
                      <YoutubeLogo size={30} className="text-red-400 mx-auto" />
                      <div className="font-medium mt-3">No matching YouTube learning path</div>
                      <p className="text-sm text-zinc-500 mt-1">
                        Try another career or skill in the search bar.
                      </p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {learningCourses.map((course) => {
                        const done = isDone(course._id);
                        const queryText = course.search_query || course.title || course.channel || "";
                        const skillList = Array.isArray(course.skills)
                          ? course.skills.slice(0, 4)
                          : tokens(queryText).slice(0, 4);
                        const watchUrl = getYoutubeWatchUrl(course);

                        return (
                          <article
                            key={course._id}
                            className="rounded-2xl border border-white/10 bg-[#0c1118] p-5 hover:border-red-400/30 hover:-translate-y-0.5 transition-all flex flex-col"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="h-10 w-10 shrink-0 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                                  <YoutubeLogo size={21} className="text-red-400" weight="fill" />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                                    YouTube Learning
                                  </div>
                                  <div className="text-xs text-zinc-400 mt-1 truncate">
                                    {course.channel || "YouTube Creator"}
                                  </div>
                                </div>
                              </div>

                            </div>

                            <h3 className="font-semibold mt-5 leading-6">{course.title}</h3>

                            <p className="text-sm text-zinc-400 mt-3 leading-6 line-clamp-3">
                              {course.description ||
                                `Learn ${queryText} through guided video lessons and practical examples.`}
                            </p>

                            <div className="mt-4 rounded-xl border border-amber-400/10 bg-amber-400/[0.04] px-3 py-2.5">
                              <div className="text-[10px] uppercase tracking-widest text-amber-300/80">
                                Why this course
                              </div>
                              <div className="text-xs text-zinc-400 mt-1">
                                {getCourseWhy(course)}
                              </div>
                            </div>

                            {skillList.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-4">
                                {skillList.map((skill) => (
                                  <Badge key={skill} tone="slate">{skill}</Badge>
                                ))}
                              </div>
                            )}

                            <div className="mt-auto pt-5 flex gap-2">
                              <a
                                href={watchUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 bg-red-600/10 border border-red-500/30 text-red-300 text-sm py-2.5 rounded-xl inline-flex items-center justify-center gap-2 hover:bg-red-600/20"
                              >
                                <YoutubeLogo size={15} />
                                Watch on YouTube
                                <ArrowSquareOut size={12} />
                              </a>

                              {!done ? (
                                <button
                                  onClick={() => markComplete(course._id, course.title)}
                                  className="border border-emerald-500/30 text-emerald-300 text-sm px-3 py-2 rounded-xl hover:bg-emerald-500/10"
                                >
                                  Done
                                </button>
                              ) : (
                                <div className="border border-emerald-500/20 text-emerald-300 text-xs px-3 py-2 rounded-xl inline-flex items-center gap-1">
                                  <Check size={14} /> Completed
                                </div>
                              )}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>

                <section className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.04] p-5">
                  <div className="flex gap-3">
                    <Sparkle size={21} className="text-amber-300 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium">Your free-learning strategy</div>
                      <p className="text-sm text-zinc-400 mt-1 leading-6">
                        Start with the recommended video path, practice the skill on a real project, then use an official learning resource before deciding whether the paid certification is worth it.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ) : tab === "free-certification" ? (
              <div className="space-y-6">
                <section>
                  <div className="flex items-end justify-between mb-4 gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Certificate size={21} className="text-emerald-300" />
                        <h2 className="text-xl font-semibold">Free Certification</h2>
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">
                        Free provider-issued certificates, digital badges and credentials you can earn without paying for the credential.
                      </p>
                    </div>
                    <Badge tone="green">FREE CREDENTIALS</Badge>
                  </div>

                  {FREE_CERTIFICATIONS.length === 0 ? (
                    <div className="rounded-2xl border border-white/10 bg-[#0c1118] p-10 text-center text-zinc-500">
                      No free certifications are available right now.
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {FREE_CERTIFICATIONS
                        .filter((cert) => {
                          const q = normalize(query);
                          if (!q) return true;
                          const searchable = normalize(
                            [
                              cert.name,
                              cert.provider,
                              cert.category,
                              cert.credentialType,
                              cert.description,
                              ...(cert.skills || []),
                              ...(cert.careerPaths || []),
                            ]
                              .filter(Boolean)
                              .join(" ")
                          );
                          return tokens(q).every((token) => searchable.includes(token));
                        })
                        .map((cert) => (
                          <article
                            key={cert.id}
                            className="rounded-2xl border border-white/10 bg-[#0c1118] p-5 hover:border-emerald-400/30 hover:-translate-y-0.5 transition-all flex flex-col"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="h-11 w-11 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                                <ProviderLogo provider={cert.provider} />
                              </div>
                              <Badge tone="green">FREE</Badge>
                            </div>

                            <h3 className="font-semibold mt-4 leading-6">{cert.name}</h3>
                            <div className="text-xs text-zinc-500 mt-1">
                              {cert.provider} · {cert.category}
                            </div>

                            <div className="flex flex-wrap gap-2 mt-3">
                              <Badge tone="green">{cert.credentialType}</Badge>
                              <Badge tone="blue">{cert.difficulty}</Badge>
                            </div>

                            <p className="text-sm text-zinc-400 mt-3 leading-6 min-h-16">
                              {cert.description}
                            </p>

                            {Array.isArray(cert.skills) && cert.skills.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-4">
                                {cert.skills.slice(0, 4).map((skill) => (
                                  <Badge key={skill} tone="slate">{skill}</Badge>
                                ))}
                              </div>
                            )}

                            <div className="mt-auto pt-5">
                              <a
                                href={cert.officialUrl || "#"}
                                target="_blank"
                                rel="noreferrer"
                                className="w-full rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 py-2.5 inline-flex items-center justify-center gap-2 text-sm hover:bg-emerald-500/15"
                              >
                                Get Free Credential
                                <ArrowSquareOut size={14} />
                              </a>
                            </div>
                          </article>
                        ))}
                    </div>
                  )}
                </section>
              </div>
            ) : (
              <>
                <div className="flex items-end justify-between mb-4 gap-3">

                  <div>

                    <div className="flex items-center gap-2">

                      <Sparkle
                        size={20}
                        className="text-amber-300"
                      />

                      <h2 className="text-xl font-semibold">
                        {tab === "recommended"
                          ? "Recommended for You"
                          : "Certifications"}
                      </h2>

                    </div>

                    <p className="text-sm text-zinc-500 mt-1">
                      {tab === "recommended"
                        ? `Matched to ${
                            profile.desired_career ||
                            "your career goal"
                          } and your roadmap.`
                        : `${displayed.length} certification${
                            displayed.length === 1
                              ? ""
                              : "s"
                          } available.`}
                    </p>

                  </div>

                  {tab === "recommended" && (
                    <button
                      onClick={() =>
                        setTab("industry")
                      }
                      className="text-sm text-blue-300 hover:text-blue-200 inline-flex items-center gap-1"
                    >
                      See All
                      <ArrowRight size={14} />
                    </button>
                  )}

                </div>

                {displayed.length === 0 ? (
                  <div className="rounded-2xl border border-white/10 bg-[#0c1118] p-10 text-center text-zinc-500">
                    No certifications match
                    these filters.
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">

                    {displayed.map(
                      (cert) => (
                        <CertificationCard
                          key={cert.id}
                          cert={cert}
                          saved={isSaved(cert.id)}
                          done={isDone(
                            `cert-${cert.id}`
                          )}
                          onSave={() =>
                            toggleSaved(
                              cert.id
                            )
                          }
                          onDetails={() =>
                            setSelected(cert)
                          }
                          onComplete={() =>
                            markComplete(
                              `cert-${cert.id}`,
                              cert.name
                            )
                          }
                        />
                      )
                    )}

                  </div>
                )}

                {tab === "recommended" &&
                  roadmapRecommended.length > 0 && (
                    <div className="mt-7 rounded-2xl border border-blue-500/20 bg-blue-500/[0.04] p-5">

                      <div className="flex gap-3">

                        <WarningCircle
                          size={21}
                          className="text-blue-300 shrink-0 mt-0.5"
                        />

                        <div>

                          <div className="font-medium">
                            Why these are recommended
                          </div>

                          <p className="text-sm text-zinc-400 mt-1">
                            Career GPS AI compares
                            your target career,
                            current role, education
                            and AI roadmap
                            recommendations
                            against each
                            certification's skills
                            and career paths.
                          </p>

                        </div>
                      </div>
                    </div>
                  )}


              </>
            )}

          </section>
        </div>
      </main>

      {selected && (
        <CertificationModal
          cert={selected}
          saved={isSaved(selected.id)}
          done={isDone(
            `cert-${selected.id}`
          )}
          onSave={() =>
            toggleSaved(selected.id)
          }
          onComplete={() =>
            markComplete(
              `cert-${selected.id}`,
              selected.name
            )
          }
          onClose={() =>
            setSelected(null)
          }
        />
      )}
    </div>
  );
}

function CertificationCard({
  cert,
  saved,
  done,
  onSave,
  onDetails,
  onComplete,
}) {
  return (
    <article
      className="rounded-2xl border border-white/10 bg-[#0c1118] p-5 hover:border-blue-400/40 hover:-translate-y-0.5 transition-all flex flex-col"
      data-testid={`cert-${cert.id}`}
    >

      <div className="flex items-start justify-between gap-3">

        <div className="flex items-center gap-3">

          <div className="h-10 w-10 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center overflow-hidden">
  <ProviderLogo provider={cert.provider} />
</div>

          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
              {cert.provider}
            </div>

            <div className="text-xs text-zinc-400 mt-1">
              {cert.category}
            </div>
          </div>

        </div>

        {cert.badge && (
          <Badge
            tone={
              cert.badge ===
              "Most Relevant"
                ? "gold"
                : "blue"
            }
          >
            {cert.badge}
          </Badge>
        )}

      </div>

      <h3 className="font-semibold leading-snug mt-4">
        {cert.name}
      </h3>

      <p className="text-sm text-zinc-400 mt-2 line-clamp-3">
        {cert.description}
      </p>

      <div className="grid grid-cols-2 gap-2 mt-4 text-[11px] text-zinc-400">

        <span className="inline-flex items-center gap-1">
          <GraduationCap size={14} />
          {cert.difficulty}
        </span>

        <span className="inline-flex items-center gap-1">
          <CurrencyCircleDollar
            size={14}
          />
          {String(cert.cost || "See official website").split("—")[0].trim()}
        </span>

        <span className="inline-flex items-center gap-1">
          <Clock size={14} />
          {cert.duration}
        </span>

        <span className="inline-flex items-center gap-1">
          <Star size={14} />
          {cert.careerValue} value
        </span>

      </div>

      <div className="flex flex-wrap gap-1.5 mt-4">

        {cert.skills
          .slice(0, 3)
          .map((skill) => (
            <Badge
              key={skill}
              tone="slate"
            >
              {skill}
            </Badge>
          ))}

      </div>

      <div className="mt-auto pt-5 flex gap-2">

        <button
          onClick={onDetails}
          className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm py-2.5 inline-flex items-center justify-center gap-2"
        >
          View Details
          <ArrowRight size={14} />
        </button>

        <button
          onClick={onSave}
          aria-label="Save certification"
          className={`w-11 rounded-xl border ${
            saved
              ? "border-amber-400/40 bg-amber-400/10 text-amber-300"
              : "border-white/10 text-zinc-500"
          }`}
        >
          <BookmarkSimple
            size={18}
            weight={
              saved ? "fill" : "regular"
            }
          />
        </button>

      </div>

      {done && (
        <div className="mt-3 text-xs text-emerald-300 inline-flex items-center gap-1">
          <CheckCircle
            size={14}
            weight="fill"
          />
          Completed +20 XP
        </div>
      )}

      {!done && (
        <button
          onClick={onComplete}
          className="mt-3 text-xs text-zinc-500 hover:text-emerald-300"
        >
          Mark as completed
        </button>
      )}

    </article>
  );
}

function CertificationModal({
  cert,
  saved,
  done,
  onSave,
  onComplete,
  onClose,
}) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm p-4 sm:p-6 flex items-center justify-center"
      onMouseDown={(e) => {
        if (
          e.target === e.currentTarget
        ) {
          onClose();
        }
      }}
    >

      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border border-blue-400/20 bg-[#0a1017] shadow-2xl">

        <div className="sticky top-0 z-10 bg-[#0a1017]/95 backdrop-blur border-b border-white/10 p-5 flex items-start justify-between gap-4">

          <div>

            <div className="flex items-center gap-2">

              <Badge tone="blue">
                {cert.provider}
              </Badge>

              <Badge
                tone={
                  cert.careerValue ===
                  "High"
                    ? "gold"
                    : "slate"
                }
              >
                {cert.careerValue}{" "}
                Career Value
              </Badge>

            </div>

            <h2 className="text-xl sm:text-2xl font-semibold mt-3">
              {cert.name}
            </h2>

            <p className="text-sm text-zinc-500 mt-1">
              {cert.recommendedFor}
            </p>

          </div>

          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center"
          >
            <X size={18} />
          </button>

        </div>

        <div className="p-5 sm:p-6">

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

            <div className="flex flex-wrap gap-2">

              <Badge>
                {cert.difficulty}
              </Badge>

              <Badge>
                {cert.duration}
              </Badge>

              <Badge>
                {cert.cost}
              </Badge>

            </div>

            <a
              href={cert.officialUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl bg-amber-300 hover:bg-amber-200 text-black font-medium text-sm px-4 py-2.5 inline-flex items-center justify-center gap-2"
            >
              Visit Official Website
              <ArrowSquareOut
                size={14}
              />
            </a>

          </div>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-7">

            <div>

              <h3 className="font-semibold">
                About this certification
              </h3>

              <p className="text-sm text-zinc-400 leading-6 mt-2">
                {cert.description}
              </p>

              <h3 className="font-semibold mt-7">
                Skills you'll gain /
                validate
              </h3>

              <div className="grid sm:grid-cols-2 gap-2 mt-3">

                {cert.skills.map(
                  (skill) => (
                    <div
                      key={skill}
                      className="rounded-lg bg-white/[0.03] border border-white/10 p-2.5 text-sm text-zinc-300 inline-flex items-center gap-2"
                    >
                      <Check
                        size={14}
                        className="text-emerald-400"
                      />
                      {skill}
                    </div>
                  )
                )}

              </div>

            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4 space-y-4">

              <Detail
                icon={GraduationCap}
                label="Difficulty"
                value={cert.difficulty}
              />

              <Detail
                icon={CurrencyCircleDollar}
                label="Exam / cost"
                value={cert.cost}
              />

              <Detail
                icon={Clock}
                label="Preparation"
                value={cert.duration}
              />

              <Detail
                icon={Briefcase}
                label="Recommended for"
                value={cert.recommendedFor}
              />

              <Detail
                icon={Certificate}
                label="Eligibility"
                value={cert.eligibility}
              />

            </div>

          </div>

          <div className="mt-7 rounded-xl border border-white/10 p-4">

            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Relevant career paths
            </div>

            <div className="flex flex-wrap gap-2 mt-3">

              {cert.careerPaths.map(
                (path) => (
                  <Badge
                    key={path}
                    tone="green"
                  >
                    {path}
                  </Badge>
                )
              )}

            </div>

          </div>

          <div className="mt-5 flex flex-wrap gap-2">

            <a
              href={cert.prepUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl border border-blue-500/30 bg-blue-500/10 text-blue-300 px-4 py-2.5 text-sm inline-flex items-center gap-2"
            >
              Official Preparation
              <ArrowSquareOut
                size={14}
              />
            </a>

            <button
              onClick={onSave}
              className="rounded-xl border border-white/10 px-4 py-2.5 text-sm text-zinc-300 inline-flex items-center gap-2"
            >
              <BookmarkSimple
                size={15}
                weight={
                  saved
                    ? "fill"
                    : "regular"
                }
              />

              {saved
                ? "Saved"
                : "Save for later"}
            </button>

            {!done && (
              <button
                onClick={onComplete}
                className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 px-4 py-2.5 text-sm inline-flex items-center gap-2"
              >
                <CheckCircle
                  size={15}
                />
                Mark completed
              </button>
            )}

          </div>

        </div>
      </div>
    </div>
  );
}

function Detail({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="flex gap-3">

      <Icon
        size={17}
        className="text-blue-300 mt-0.5 shrink-0"
      />

      <div>

        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {label}
        </div>

        <div className="text-sm text-zinc-300 mt-1 leading-5">
          {value}
        </div>

      </div>

    </div>
  );
}
